import * as React from "react"
import { Loader2 } from "lucide-react"

import { Button } from "@/components/ui/button"

export type ConnectBrokerWizardFooterAction = {
  label: string
  onClick: () => void
  disabled?: boolean
  isLoading?: boolean
  loadingLabel?: string
}

export type ConnectBrokerWizardDialogFooterProps = {
  onCancel?: () => void
  onBack?: () => void
  onContinue?: () => void
  continueDisabled?: boolean
  continueLabel?: string
  cancelLabel?: string
  showContinue?: boolean
  showBack?: boolean
  primaryFooterAction?: ConnectBrokerWizardFooterAction
}

/** Optional Cancel (left) + Back / Continue or lifted primary action (right). */
export function ConnectBrokerWizardDialogFooter({
  onCancel,
  onBack,
  onContinue,
  continueDisabled = false,
  continueLabel = "Continue",
  cancelLabel = "Cancel",
  showContinue = true,
  showBack = false,
  primaryFooterAction,
}: ConnectBrokerWizardDialogFooterProps): React.ReactElement {
  return (
    <div
      data-connect-broker-wizard-footer=""
      className={
        onCancel
          ? "flex w-full flex-wrap items-center justify-between gap-3"
          : "flex w-full flex-wrap items-center justify-end gap-3"
      }
    >
      {onCancel ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-font="primary"
          data-trade-log-action="delete"
          className="h-8 shrink-0 border-destructive/40 px-5 text-[0.75rem] normal-case tracking-normal text-destructive shadow-sm hover:bg-destructive/10 hover:text-destructive focus-visible:border-destructive/60 focus-visible:ring-destructive/25 dark:border-destructive/50 dark:hover:bg-destructive/15"
          onClick={onCancel}
        >
          <span data-font="primary" className="truncate normal-case tracking-normal">
            {cancelLabel}
          </span>
        </Button>
      ) : null}
      <div className="flex flex-nowrap items-center gap-3">
        {showBack && onBack ? (
          <Button
            type="button"
            variant="outline-gray"
            size="sm"
            data-font="primary"
            data-reports-rename-button=""
            className="h-8 shrink-0 px-5 text-[0.75rem] normal-case tracking-normal"
            onClick={onBack}
          >
            <span data-font="primary" className="truncate normal-case tracking-normal">
              Back
            </span>
          </Button>
        ) : null}
        {primaryFooterAction ? (
          <Button
            type="button"
            variant="gradient"
            size="sm"
            data-font="primary"
            className="h-8 shrink-0 px-5 text-[0.75rem] normal-case tracking-normal"
            onClick={primaryFooterAction.onClick}
            disabled={primaryFooterAction.disabled || primaryFooterAction.isLoading}
          >
            {primaryFooterAction.isLoading ? (
              <>
                <Loader2 className="size-4 animate-spin" aria-hidden />
                {primaryFooterAction.loadingLabel ?? "Saving…"}
              </>
            ) : (
              primaryFooterAction.label
            )}
          </Button>
        ) : showContinue && onContinue ? (
          <Button
            type="button"
            variant="gradient"
            size="sm"
            data-font="primary"
            className="h-8 shrink-0 px-5 text-[0.75rem] normal-case tracking-normal"
            onClick={onContinue}
            disabled={continueDisabled}
          >
            {continueLabel}
          </Button>
        ) : null}
      </div>
    </div>
  )
}
